import { useEffect, useState } from 'react'

import { api, identityHeaders, unwrap } from '../api/client'
import { useSealedQuery } from '../api/sealed'
import type { Feed, FeedItem, Lookups } from '../api/types'
import { looksLikeId, nameFor, useLookups, usePersonNames, type PersonNames } from '../app/lookups'
import { useSessionEra } from '../documents/held'
import { t, tOr } from '../i18n'
import { ErrorNotice, Pending, formatDateTime } from '../ui'
import { feedRows } from './feed'

/**
 * What has happened on this project, live.
 *
 * The feed reads through the same cursor map everything else in the platform
 * resumes on — `{vm: 184102, sbc_a: 4390}` — so "I am behind" and "I am
 * current" are distinguishable per origin, which one scalar across three
 * writers cannot do. The card says which it is rather than showing stale rows
 * as though they were fresh: never show live data without showing its age.
 *
 * Server-sent events rather than the WebSocket. The socket carries presence
 * and acknowledgements back up and is what a handheld wants; a browser in an
 * office is behind whatever proxy the owner's IT department runs, and SSE is
 * the path that survives it.
 *
 * No system text is stored in English, so a row is a message key plus its
 * arguments and the client resolves it. A key this build does not know renders
 * as the verb, which is honest, and shows up in a screenshot as the missing
 * string it is.
 */

function summarise(item: FeedItem, refs: Lookups | undefined, people: PersonNames): string {
  const args: Record<string, string> = {}
  for (const [name, value] of Object.entries(item.summary_args ?? {})) {
    // Identifiers go through the project's own lists on the way in. Every
    // module writes its actor as a bare person id, so a summary rendered
    // straight from the arguments reads out a UUID at the front of the
    // sentence — which is the one part of the row a person actually reads.
    // The actor is known to be a person, so it is named whether or not they
    // are a member: a Global Admin acts on jobs they are not on.
    if (name === 'actor' && typeof value === 'string' && looksLikeId(value)) {
      args[name] = people.nameOf(value)
      continue
    }
    args[name] = nameFor(refs, typeof value === 'string' ? value : JSON.stringify(value))
  }
  const template = tOr(item.summary_key, '')
  if (!template) return `${item.entity_type} ${item.verb}`
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => args[name] ?? whole)
}

/**
 * Whose authority the actor used, when it was lent (spec 4 8: "Jordan acting
 * as backup for Alex"): the principal at the head of each chain. A row from
 * the page carries the chains as a field; a frame from the stream carries the
 * whole event, and the chains are in its payload under the same name.
 */
function actingFor(item: FeedItem): string[] {
  const chains =
    item.acting_as_backup ??
    ((item as { payload?: Record<string, unknown> }).payload?.acting_as_backup as
      | string[][]
      | undefined) ??
    []
  return [...new Set(chains.map((chain) => chain[0]).filter((one): one is string => !!one))]
}

/**
 * The newest page of the project's feed, read under this session and sealed
 * on a refusal. Shared, so the activity chart reads the same answer from the
 * cache rather than asking again, and a refusal takes both away at once.
 *
 * `data` is withheld while the read is sealed, so nothing drawn from it -- the
 * rows, the chart, the stream that resumes from its cursor -- outlives the
 * instance saying no, or the session that asked.
 */
export function useFeed(projectId: string) {
  const era = useSessionEra()
  const sealed = useSealedQuery({
    key: ['engage', 'feed', projectId],
    era,
    scope: `engage/feed/${projectId}`,
    read: async (): Promise<Feed> =>
      unwrap(
        await api.GET('/engage/feed', {
          params: { query: { project_id: projectId } },
        }),
      ),
  })
  return { ...sealed, era }
}

/** How many rows the activity card shows before it is opened. */
export const FEED_SHOWN = 6

export function FeedCard({ projectId }: { projectId: string }) {
  // Cached for the session and already fetched by every form on this project,
  // so naming the actor costs the feed no round trip of its own.
  const refs = useLookups(projectId)
  const people = usePersonNames(projectId)
  const [live, setLive] = useState(false)
  const [streamed, setStreamed] = useState<FeedItem[]>([])

  // The rows this card holds are the only project data on a project screen
  // that is not inside a query key, because they arrived on the stream rather
  // than from a read. A project switch leaves this component mounted — the
  // route element is the same one — so without this they are carried across
  // and rendered under the next project's heading. Cleared while rendering
  // rather than in an effect, so there is no frame in which they are on screen
  // next to the wrong project.
  //
  // The same holds for a change of session: rows streamed to the last person
  // at this browser are not the next one's to see.
  const { query: feed, data, era } = useFeed(projectId)
  const holder = `${projectId}/${era}`
  const [shownFor, setShownFor] = useState(holder)
  if (shownFor !== holder) {
    setShownFor(holder)
    setStreamed([])
  }

  useEffect(() => {
    // The stream resumes from where the first read finished, so the card never
    // shows a row twice and never skips one between the read and the open.
    if (!data) return
    // The position comes off the read itself rather than a ref beside it. A ref
    // is not scoped to anything, so the last project read set the position the
    // next stream opened at; the read is keyed by project, so this cannot be
    // another project's place in the log.
    const query = new URLSearchParams({ project_id: projectId })
    if (data.cursor) query.set('cursor', JSON.stringify(data.cursor))

    // Read with fetch rather than EventSource. EventSource cannot set a
    // request header, so in trusted-header mode it reaches the hub with no
    // identity and is refused; this sends the same header every other call
    // sends and parses the frames itself, which is a dozen lines.
    const abort = new AbortController()

    // This run of the effect, as something the work it starts can ask about.
    //
    // Aborting the request is not enough and that is the whole of the second
    // finding here. `abort()` stops the fetch; it does nothing about work that
    // is already queued — a response that is about to resolve, a chunk already
    // read, the `finally` of a reader that is about to end. Every one of those
    // lands after the switch, in a closure holding the *old* project's stream,
    // and every one of them calls a setter on a component that has moved on.
    //
    // The project id cannot stand in for this. A card switched from A to B and
    // back to A has two A generations, and a callback from the first would find
    // the id it expects. So the lifetime is this closure and nothing else, and
    // the cleanup invalidates it as well as aborting.
    let current = true

    void (async () => {
      try {
        const response = await fetch(`/engage/stream?${query.toString()}`, {
          headers: { ...identityHeaders(), Accept: 'text/event-stream' },
          credentials: 'same-origin',
          signal: abort.signal,
        })
        if (!response.ok || !response.body) return
        // The response can arrive after the switch: the request was aborted,
        // and this promise was already on its way to resolving.
        if (!current) return
        setLive(true)
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
        let buffer = ''
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += value
          // Frames are separated by a blank line; a partial one waits for the
          // next chunk rather than being parsed half-read.
          let split = buffer.indexOf('\n\n')
          while (split !== -1) {
            handleFrame(buffer.slice(0, split))
            buffer = buffer.slice(split + 2)
            split = buffer.indexOf('\n\n')
          }
        }
      } catch {
        /* the reader was aborted, or the connection dropped */
      } finally {
        // An old stream ending must not mark the new one disconnected. This
        // is the same closure's own chip or nobody's.
        if (current) setLive(false)
      }
    })()

    function handleFrame(frame: string): void {
      const data = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trim())
        .join('\n')
      if (!data) return
      if (!current) return
      try {
        const parsed: unknown = JSON.parse(data)
        if (typeof parsed === 'object' && parsed !== null && 'summary_key' in parsed) {
          setStreamed((rows) => [parsed as FeedItem, ...rows].slice(0, 30))
        }
      } catch {
        /* a heartbeat or a frame this build does not read */
      }
    }

    return () => {
      current = false
      abort.abort()
      setLive(false)
    }
  }, [data, projectId])

  // Nothing at all while the read is sealed: the streamed rows were let in by
  // the read the instance has since refused.
  const rows = data ? feedRows(streamed, data.items) : []
  // The card was as tall as the page it was given, which on a busy job pushed
  // everything else on the overview off the screen. It shows the newest few
  // and opens the rest on request, in a box that scrolls by itself.
  const [open, setOpen] = useState(false)
  const shown = open ? rows : rows.slice(0, FEED_SHOWN)

  return (
    <section className="card" aria-labelledby="feed-heading">
      <div className="row">
        <h3 id="feed-heading" style={{ flex: 1 }}>
          {t('engage.feed.title')}
        </h3>
        <span className="chip" data-tone={live ? 'good' : undefined}>
          {live ? t('engage.feed.live') : t('engage.feed.disconnected')}
        </span>
      </div>
      {feed.isPending ? <Pending /> : null}
      {feed.error ? <ErrorNotice error={feed.error} retry={() => void feed.refetch()} /> : null}
      {data?.behind ? <p className="muted">{t('engage.feed.behind')}</p> : null}
      {data && rows.length === 0 ? <p className="muted">{t('engage.feed.empty')}</p> : null}
      <ul className="feed" data-open={open}>
        {shown.map((item) => (
          <li key={`${item.origin}:${item.origin_seq}`}>
            <div>{summarise(item, refs.data, people)}</div>
            {actingFor(item).map((principal) => (
              <div key={principal} className="hint">
                {t('engage.feed.acting_as_backup', { name: people.nameOf(principal) })}
              </div>
            ))}
            <span className="when">
              {formatDateTime(item.occurred_at)} · <span className="mono">{item.origin}</span>
            </span>
          </li>
        ))}
      </ul>
      {rows.length > FEED_SHOWN ? (
        <button type="button" className="text-link" onClick={() => setOpen((was) => !was)}>
          {open ? t('engage.feed.show_fewer') : t('engage.feed.show_all', { count: rows.length })}
        </button>
      ) : null}
    </section>
  )
}
